import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  healthHeartbeatDomain,
  healthHeartbeatTicks,
  type HealthHeartbeatPoint,
} from './healthHeartbeat';

const SERIES = [
  { key: 'healthy', label: 'Healthy', color: '#16a34a' },
  { key: 'outOfSync', label: 'Out of sync', color: '#d97706' },
  { key: 'downRisk', label: 'Down risk', color: '#dc2626' },
  { key: 'na', label: 'N/A', color: '#64748b' },
] as const;

function formatTick(ms: number) {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const HealthHeartbeatChart: React.FC<{ data: HealthHeartbeatPoint[] }> = ({ data }) => {
  const domain = useMemo(() => healthHeartbeatDomain(data), [data]);
  const ticks = useMemo(() => healthHeartbeatTicks(domain), [domain]);

  if (data.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        No health heartbeat yet. A point is recorded after each agent-balance sync.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          type="number"
          dataKey="ts"
          domain={domain}
          ticks={ticks}
          tickFormatter={formatTick}
          tick={{ fontSize: 10, fill: '#64748b' }}
          tickLine={false}
          axisLine={false}
          minTickGap={8}
        />
        <YAxis
          allowDecimals={false}
          domain={[0, (max: number) => Math.max(4, Math.ceil(Number(max) || 0))]}
          padding={{ top: 6, bottom: 10 }}
          tick={{ fontSize: 11, fill: '#64748b' }}
          tickLine={false}
          axisLine={false}
          width={28}
        />
        <Tooltip
          labelFormatter={(label) => formatTick(Number(label))}
          formatter={(value: number, name: string) => [value, name]}
          contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
        />
        <Legend
          verticalAlign="top"
          height={24}
          iconType="circle"
          wrapperStyle={{ fontSize: 11 }}
        />
        {SERIES.map((series) => (
          <Line
            key={series.key}
            isAnimationActive={false}
            type="monotone"
            dataKey={series.key}
            name={series.label}
            stroke={series.color}
            strokeWidth={1.5}
            connectNulls
            dot={{ r: 2, fill: series.color, strokeWidth: 0 }}
            activeDot={{ r: 3, strokeWidth: 1, stroke: '#ffffff' }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default HealthHeartbeatChart;
